import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, Settings as SettingsIcon, Compass, LayoutGrid, Link2, Plus, Wallet, Users, Trophy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandLockup } from "@/components/Brand";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function TopBar({ right }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, logout } = useAuth();

  const navCls = (active) =>
    `flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
      active ? "bg-[#35606E] text-white" : "text-[#35606E] hover:bg-[#35606E]/10"
    }`;
  const onArtists = pathname === "/" || pathname.startsWith("/artists") || pathname.startsWith("/songs") || pathname.startsWith("/campaigns");
  const onDiscovery = pathname.startsWith("/discovery");
  const onTours = pathname.startsWith("/tours") || pathname.startsWith("/tour/");
  const onBudget = pathname.startsWith("/budget");
  const onTeam = pathname.startsWith("/team");
  const isAdmin = !!user?.is_admin;

  return (
    <header className="sticky top-0 z-40 border-b border-[#E7DFCC] bg-[#F7EFDD]/88 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-4 py-3.5 sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-2 sm:gap-4">
          <BrandLockup onClick={() => navigate("/")} />
          <nav className="hidden items-center gap-1 sm:flex">
            <button data-testid="nav-artists" aria-label="Artists" onClick={() => navigate("/")} className={navCls(onArtists)}>
              <LayoutGrid className="h-4 w-4" /> <span>Artists</span>
            </button>
            <button data-testid="nav-discovery" aria-label="Discovery" onClick={() => navigate("/discovery")} className={navCls(onDiscovery)}>
              <Compass className="h-4 w-4" /> <span>Discovery</span>
            </button>
            {isAdmin && (
              <button data-testid="nav-tours" aria-label="Tour Leaderboard" onClick={() => navigate("/tours")} className={navCls(onTours)}>
                <Trophy className="h-4 w-4" /> <span>Tour Leaderboard</span>
              </button>
            )}
            {isAdmin && (
              <button data-testid="nav-budget" aria-label="Budget Trackers" onClick={() => navigate("/budget")} className={navCls(onBudget)}>
                <Wallet className="h-4 w-4" /> <span>Budgets</span>
              </button>
            )}
            {isAdmin && (
              <button data-testid="nav-team" aria-label="Team" onClick={() => navigate("/team")} className={navCls(onTeam)}>
                <Users className="h-4 w-4" /> <span>Team</span>
              </button>
            )}
          </nav>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <div className="hidden items-center gap-2 sm:flex sm:gap-3">{right}</div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#35606E] text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
              >
                {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 rounded-xl">
              <div className="px-2 py-1.5 text-xs text-neutral-500">{user?.email}</div>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-discovery" onClick={() => navigate("/discovery")} className="sm:hidden">
                <Compass className="mr-2 h-4 w-4" /> Discovery
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem data-testid="menu-tours" onClick={() => navigate("/tours")} className="sm:hidden">
                  <Trophy className="mr-2 h-4 w-4" /> Tour Leaderboard
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <DropdownMenuItem data-testid="menu-budget" onClick={() => navigate("/budget")} className="sm:hidden">
                  <Wallet className="mr-2 h-4 w-4" /> Budgets
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <DropdownMenuItem data-testid="menu-team" onClick={() => navigate("/team")} className="sm:hidden">
                  <Users className="mr-2 h-4 w-4" /> Team
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="menu-import" onClick={() => navigate("/?import=1")} className="sm:hidden">
                <Link2 className="mr-2 h-4 w-4" /> Import link
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-track" onClick={() => navigate("/?track=1")} className="sm:hidden">
                <Plus className="mr-2 h-4 w-4" /> Track a Sound
              </DropdownMenuItem>
              <DropdownMenuSeparator className="sm:hidden" />
              <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/settings")}>
                <SettingsIcon className="mr-2 h-4 w-4" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="text-red-600 focus:text-red-600">
                <LogOut className="mr-2 h-4 w-4" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
